import React from "react";
import { PatientRow, Patient } from "./PatientRow";

interface PatientTableProps {
  patients: Patient[];
  onDelete?: (patientId: string) => void;
  onRestore?: (patientId: string) => void;
  onPermanentDelete?: (patientId: string) => void;
  onView?: (patient: Patient) => void;
  filterOption?: string;
  onFilterChange?: (option: string) => void;
}

export const PatientTable: React.FC<PatientTableProps> = ({
  patients,
  onDelete,
  onRestore,
  onPermanentDelete,
  onView,
  filterOption = "all",
  onFilterChange,
}) => {
  return (
    <div
      className="flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white"
      style={{
        boxShadow: '0px 4px 6px -4px #0000001A, 0px 10px 15px -3px #0000001A',
      }}
    >
      {/* Fixed Header - Desktop Only */}
      <div
        className="hidden flex-shrink-0 border-b border-slate-200 md:block"
        style={{
          background: "linear-gradient(90deg, #ECFEFF 0%, #EFF6FF 100%)",
        }}
      >
        <table className="w-full">
          <thead>
            <tr>
              <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500" style={{ width: '22%' }}>
                Patient Name
              </th>
              <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500" style={{ width: '8%' }}>
                Age
              </th>
              <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500" style={{ width: '10%' }}>
                Gender
              </th>
              <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500" style={{ width: '8%' }}>
                Blood
              </th>
              <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500" style={{ width: '16%' }}>
                Contact
              </th>
              <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500" style={{ width: '12%' }}>
                Last Visit
              </th>
              <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500" style={{ width: '10%' }}>
                Status
              </th>
              <th className="px-4 py-3 text-center text-[10px] font-semibold uppercase tracking-wider text-slate-500" style={{ width: '14%' }}>
                Actions
              </th>
            </tr>
          </thead>
        </table>
      </div>

      {/* Mobile Header with Filter */}
      <div
        className="flex-shrink-0 border-b border-slate-200 px-4 py-3 md:hidden"
        style={{
          background: "linear-gradient(90deg, #ECFEFF 0%, #EFF6FF 100%)",
        }}
      >
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            Patients
          </span>
          {/* Mobile Filter Dropdown */}
          <div className="relative">
            <select
              value={filterOption}
              onChange={(e) => onFilterChange?.(e.target.value)}
              className="h-7 appearance-none rounded-lg border border-slate-200 bg-white px-3 pr-7 text-xs text-slate-700"
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <div className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m6 9 6 6 6-6"/>
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="md:flex-1">
        {patients.length === 0 ? (
          <div className="flex h-full items-center justify-center py-16">
            <p className="text-base text-slate-500">No results found</p>
          </div>
        ) : (
          <>
            {/* Mobile Card List */}
            <div className="md:hidden">
              {patients.map((patient) => (
                <PatientRow
                  key={patient.id}
                  patient={patient}
                  onDelete={onDelete}
                  onRestore={onRestore}
                  onPermanentDelete={onPermanentDelete}
                  onView={onView}
                  variant="mobile"
                />
              ))}
            </div>

            {/* Desktop Table */}
            <table className="hidden w-full md:table">
              <tbody>
                {patients.map((patient) => (
                  <PatientRow
                    key={patient.id}
                    patient={patient}
                    onDelete={onDelete}
                    onRestore={onRestore}
                    onPermanentDelete={onPermanentDelete}
                    onView={onView}
                    variant="desktop"
                  />
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
};

export default PatientTable;
